import React from 'react';

const AboutPage = () => {
  const features = [
    {
      title: 'AI-Powered Analysis',
      description: 'Gemini AI evaluates educational quality across 6 comprehensive categories with detailed scoring.'
    },
    {
      title: 'Multi-Language Support',
      description: 'Analyze Hindi, Hinglish, and English content with language-specific insights.'
    },
    {
      title: 'Lightning Fast',
      description: 'Instant results (0.15s) with S3-backed caching across all your devices.'
    },
    {
      title: 'Video Leaderboard',
      description: 'Discover top-rated educational content, auto-updated every 30 seconds.'
    },
    {
      title: 'Comment Analysis',
      description: 'Understand audience sentiment and engagement from YouTube comments.'
    },
    {
      title: 'Cross-Device Sync',
      description: 'Access all your analyses from anywhere - data stored securely in AWS S3.'
    }
  ];

  const howItWorks = [
    { step: '1', title: 'Paste URL', description: 'Any YouTube format: youtube.com, youtu.be, shorts, embed' },
    { step: '2', title: 'Extract Transcript', description: '6 fallback methods ensure 99% success rate' },
    { step: '3', title: 'AI Analysis', description: 'Gemini evaluates content quality and teaching effectiveness' },
    { step: '4', title: 'Get Report', description: 'Detailed scores, strengths, weaknesses, and suggestions' }
  ];

  const generateParticles = () => {
    const particles = [];
    const colors = ['#8b5cf6', '#ec4899', '#06b6d4', '#10b981'];
    
    for (let i = 0; i < 30; i++) {
      particles.push(
        <div
          key={i}
          className="particle"
          style={{
            width: Math.random() * 4 + 2 + 'px',
            height: Math.random() * 4 + 2 + 'px',
            background: `linear-gradient(45deg, ${colors[Math.floor(Math.random() * 4)]}, transparent)`,
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
            animationDelay: Math.random() * 5 + 's'
          }}
        />
      );
    }
    return particles;
  };

  return (
    <div className="page-container about-page">
      <div className="animated-background" />

      {/* Decorative blobs */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '5%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15), transparent)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        pointerEvents: 'none',
        zIndex: 0
      }} />
      <div style={{
        position: 'absolute',
        bottom: '15%',
        right: '10%',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(236, 72, 153, 0.15), transparent)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <div className="floating-particles">{generateParticles()}</div>

      <div className="about-content" style={{ position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto', padding: '80px 20px 40px' }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h1 style={{
            fontSize: '3.5rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            About Gradi.AI
          </h1>
          <p style={{ fontSize: '1.3rem', color: '#d1d5db', maxWidth: '700px', margin: '0 auto', marginBottom: '2rem' }}>
            AI-powered analysis to help educators improve and students find the best content
          </p>
          
          {/* How to Use Video */}
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            marginBottom: '3rem',
            background: 'rgba(15, 15, 35, 0.9)',
            border: '2px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '20px',
            padding: '1.5rem',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#e5e7eb',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              How to Use Gradi.AI
            </h2>
            <div style={{
              position: 'relative',
              paddingBottom: '56.25%', // 16:9 aspect ratio
              height: 0,
              overflow: 'hidden',
              borderRadius: '12px',
              background: '#000'
            }}>
              <iframe
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                title="How to Use Gradi.AI"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div style={{ marginBottom: '4rem' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '2rem'
          }}>
            {features.map((feature, index) => (
              <div
                key={index}
                style={{
                  background: 'rgba(15, 15, 35, 0.9)',
                  border: '2px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '20px',
                  padding: '2rem',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                }}
              >
                <h3 style={{ fontSize: '1.3rem', fontWeight: '700', marginBottom: '1rem', color: '#e5e7eb' }}>
                  {feature.title}
                </h3>
                <p style={{ color: '#9ca3af', lineHeight: '1.6', fontSize: '1rem' }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* How It Works */}
        <div style={{ marginBottom: '4rem' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            marginBottom: '2.5rem',
            background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}>
            How It Works
          </h2>
          <div style={{ display: 'grid', gap: '1.5rem', maxWidth: '800px', margin: '0 auto' }}>
            {howItWorks.map((item, index) => (
              <div
                key={index}
                style={{
                  background: 'rgba(15, 15, 35, 0.9)',
                  border: '2px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '16px',
                  padding: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.5rem',
                  backdropFilter: 'blur(20px)'
                }}
              >
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: 'white',
                  flexShrink: 0
                }}>
                  {item.step}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#e5e7eb', marginBottom: '0.5rem' }}>
                    {item.title}
                  </h3>
                  <p style={{ color: '#9ca3af', fontSize: '0.95rem', lineHeight: '1.5', margin: 0 }}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '2rem',
          marginBottom: '4rem',
          maxWidth: '800px',
          margin: '0 auto 4rem'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            background: 'rgba(15, 15, 35, 0.8)',
            borderRadius: '16px',
            border: '2px solid rgba(16, 185, 129, 0.3)',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ fontSize: '3rem', fontWeight: 'bold', color: '#10b981', marginBottom: '0.5rem' }}>
              99%
            </div>
            <p style={{ color: '#9ca3af', fontSize: '1rem' }}>Transcript Success</p>
          </div>
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            background: 'rgba(15, 15, 35, 0.8)',
            borderRadius: '16px',
            border: '2px solid rgba(139, 92, 246, 0.3)',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ fontSize: '3rem', fontWeight: 'bold', color: '#8b5cf6', marginBottom: '0.5rem' }}>
              0.15s
            </div>
            <p style={{ color: '#9ca3af', fontSize: '1rem' }}>Cached Load Time</p>
          </div>
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            background: 'rgba(15, 15, 35, 0.8)',
            borderRadius: '16px',
            border: '2px solid rgba(236, 72, 153, 0.3)',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ fontSize: '3rem', fontWeight: 'bold', color: '#ec4899', marginBottom: '0.5rem' }}>
              Top 10
            </div>
            <p style={{ color: '#9ca3af', fontSize: '1rem' }}>Best Videos Ranked</p>
          </div>
        </div>

        {/* Call to Action */}
        <div style={{ textAlign: 'center', paddingBottom: '2rem' }}>
          <p style={{ fontSize: '1.3rem', color: '#9ca3af', marginBottom: '2rem' }}>
            Ready to discover the best educational content?
          </p>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '1.25rem 3rem',
              background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              fontSize: '1.15rem',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 10px 30px rgba(139, 92, 246, 0.4)',
              fontFamily: 'inherit'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = '0 15px 40px rgba(139, 92, 246, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 10px 30px rgba(139, 92, 246, 0.4)';
            }}
          >
            Start Analyzing Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
